import type {
  PolymorphicComponentProps,
  PolymorphicComponentPropsWithRef,
  PolymorphicRef,
} from '@/common';
import type { ElementType, ReactNode } from 'react';
import { forwardRef } from 'react';

import { isNullish, valueOrFallback } from '@ballerine/common';

import { ctw } from '@/common';

export type TTextWithNAFallback = <TElement extends ElementType = 'span'>(
  props: PolymorphicComponentPropsWithRef<TElement> & {
    checkFalsy?: boolean;
    renderZero?: boolean;
    disableTranslation?: boolean;
  },
) => ReactNode;

export const TextWithNAFallback: TTextWithNAFallback = forwardRef(
  // @ts-ignore
  <TElement extends ElementType = 'span'>(
    {
      as,
      children,
      className,
      checkFalsy = true,
      renderZero = true,
      disableTranslation,
      ...props
    }: PolymorphicComponentProps<TElement> & {
      checkFalsy?: boolean;
      renderZero?: boolean;
      disableTranslation?: boolean;
    },
    ref?: PolymorphicRef<TElement>,
  ) => {
    const Component = as ?? 'span';
    const valueWithFallback =
      renderZero && children === 0
        ? 0
        : valueOrFallback('N/A', {
            checkFalsy,
          })(children);
    const shouldDisableTranslation =
      disableTranslation && valueWithFallback !== 'N/A' && valueWithFallback !== null;

    return (
      <Component
        {...props}
        className={ctw(
          {
            'text-slate-400': [
              checkFalsy && !renderZero && !children,
              checkFalsy && renderZero && !children && children !== 0,
              !checkFalsy && isNullish(children),
            ].some(Boolean),
            notranslate: shouldDisableTranslation,
          },
          className,
        )}
        {...(shouldDisableTranslation && { translate: 'no' })}
        ref={ref}
      >
        {valueWithFallback}
      </Component>
    );
  },
);

// @ts-ignore
TextWithNAFallback.displayName = 'TextWithNAFallback';
